/**
 * billingAccess — shared billing-permission computeds.
 *
 * Gates the billing/payments surface on the user's billing permission FOR THE
 * CURRENT ORG. The backend (GET /api/auth/me) exposes two booleans on
 * `current_organization`:
 *
 *   - can_view_billing   — may see payment info / invoices / subscription.
 *   - can_manage_billing — may manage payment methods + change/cancel plan
 *                          (implies view).
 *
 * Both are false for a technical org-admin with no billing grant, and both are
 * true for the org owner (or an explicitly-granted member).
 *
 * Reactivity: these read off the auth store's `currentUser.current_organization`,
 * which is re-fetched via `auth/fetchMe` on every org switch. NEVER cache the
 * result in `data()` — keep it a computed so it recomputes on org switch.
 *
 * Usage (Options API):
 *   import billingAccess from '@/mixins/billingAccess'
 *   export default { mixins: [billingAccess], ... }
 *   // then use this.canViewBilling / this.canManageBilling in template & logic.
 */
import { mapGetters } from 'vuex'

export default {
  computed: {
    // `currentUser` is the merged /me payload held in the auth store. Aliased
    // so the mixin never collides with a host component that already maps a
    // differently-named getter.
    ...mapGetters('auth', { _billingCurrentUser: 'currentUser' }),

    /** The active org block from /me — source of the billing flags. */
    billingOrg() {
      return this._billingCurrentUser?.current_organization || null
    },

    /** May view payment info / invoices / subscription for the current org. */
    canViewBilling() {
      return this.billingOrg?.can_view_billing === true
    },

    /**
     * May manage payment methods + change/cancel the plan for the current org.
     * Implies view. We still OR `canViewBilling` defensively so a backend that
     * only sends manage=true never accidentally hides the view-level surface.
     */
    canManageBilling() {
      return this.billingOrg?.can_manage_billing === true
    }
  }
}
